import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    Portal,
    Tooltip,
    IconButton,
    TextField,
    Autocomplete,
    Popper,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { calculateVatAmount } from '../../../Utils/GetVatValue';
import styles from './AddJournalEntryModal.module.scss';
import { parseSmartDateInput } from '../../../Utils/parseSmartDateInput';

/** Reorderable columns, in default order. `documents` is always rendered last. */
const COLUMNS = {
    sortCodeId: { titleKey: 'Code ID', size: 'grow' },
    debitAccount: { titleKey: 'Debit Account', size: 'grow' },
    creditAccount: { titleKey: 'Credit Account', size: 'grow' },
    vat: { titleKey: 'VAT', size: 'narrow' },
    amount: { titleKey: 'Amount', required: true },
    documentDate: { titleKey: 'Document Date', size: 'date' },
    uploadDate: { titleKey: 'Upload Date', size: 'date' },
    accountCaseId: { titleKey: 'CompanyId' },
    confirmationNumber: { titleKey: 'ConfirmationNumber' },
    reference: { titleKey: 'Reference' },
    reference2: { titleKey: 'Reference2' },
    description: { titleKey: 'Description', size: 'wide' },
};
const DEFAULT_COLUMN_ORDER = Object.keys(COLUMNS);
const ROW_END_FIELD = 'documents';
const COLUMN_ORDER_STORAGE_KEY = 'addJournalEntryModal.columnOrder';
const COLUMN_DRAG_MIME = 'application/x-journal-entry-column';

const AUTO_OPEN_FIELDS = new Set(['sortCodeId', 'debitAccount', 'creditAccount', 'vat']);
/** Single-line text inputs where plain ArrowUp/ArrowDown have no other meaning. */
const ARROW_ROW_NAV_FIELDS = new Set([
    'amount',
    'accountCaseId',
    'confirmationNumber',
    'reference',
    'reference2',
    'description',
]);

const IS_MAC = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
const MOD_KEY_LABEL = IS_MAC ? '⌘' : 'Ctrl';
const ALT_KEY_LABEL = IS_MAC ? '⌥' : 'Alt';

const loadColumnOrder = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(COLUMN_ORDER_STORAGE_KEY));
        if (!Array.isArray(saved)) return DEFAULT_COLUMN_ORDER;
        const known = [...new Set(saved.filter((key) => COLUMNS[key]))];
        return [...known, ...DEFAULT_COLUMN_ORDER.filter((key) => !known.includes(key))];
    } catch {
        return DEFAULT_COLUMN_ORDER;
    }
};

const moveItem = (list, from, to) => {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

const getCellFromTarget = (target) => {
    const fieldEl = target?.closest?.('[data-field]');
    const rowEl = fieldEl?.closest('[data-row-id]');
    if (!fieldEl || !rowEl) return null;
    return { rowId: rowEl.dataset.rowId, fieldName: fieldEl.dataset.field };
};

let draftSeq = 0;
const nextDraftId = () => {
    draftSeq += 1;
    return `draft-${Date.now()}-${draftSeq}`;
};

const createEmptyDraftRow = () => ({
    id: nextDraftId(),
    accountCodeName: '',
    accountCaseId: '',
    accountCodeId: '',
    reference: '',
    reference2: '',
    description: '',
    uploadDate: new Date(),
    documentDate: new Date(),
    amount: '',
    vat: 0,
    vatAmount: 0,
    netAmount: 0,
    creditAccount: '',
    debitAccount: '',
    sortCodeId: '3',
    processed: false,
    confirmationNumber: '',
    journalEntryType: 0,
    documents: [],
    addAsProcessed: true,
});

const isBlankDraft = (row) => (
    !row?.amount
    && !row?.debitAccount
    && !row?.creditAccount
    && !row?.reference
    && !row?.reference2
    && !row?.description
    && !row?.confirmationNumber
    && !row?.accountCaseId
    && !(row?.documents?.length)
);

const fieldHasCommittedValue = (row, fieldName) => {
    if (!row) return false;
    if (fieldName === 'sortCodeId') return row.sortCodeId != null && row.sortCodeId !== '';
    if (fieldName === 'debitAccount') return !!row.debitAccount;
    if (fieldName === 'creditAccount') return !!row.creditAccount;
    if (fieldName === 'vat') return row.vat != null && row.vat !== '';
    return true;
};

/** Safely evaluate amount expressions like "400+600" → 1000. */
const evaluateAmountExpression = (raw) => {
    if (raw == null) return null;
    const str = String(raw).trim().replace(/\s+/g, '');
    if (!str) return null;

    if (/^-?\d+(\.\d+)?$/.test(str)) {
        const n = Number(str);
        return Number.isFinite(n) ? n : null;
    }

    // Only digits, decimal point, and + - * / ( )
    if (!/^[0-9+\-*/().]+$/.test(str)) return null;

    try {
        // eslint-disable-next-line no-new-func
        const result = Function(`"use strict"; return (${str});`)();
        if (typeof result !== 'number' || !Number.isFinite(result)) return null;
        return Math.round(result * 10000) / 10000;
    } catch {
        return null;
    }
};

const sameWidthModifier = {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        const width = `${state.rects.reference.width}px`;
        state.styles.popper.width = width;
        state.styles.popper.minWidth = width;
    },
};

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => {
    const { className, style, modifiers, ...other } = props;
    const width = props.anchorEl?.clientWidth ?? style?.width;
    return (
        <Popper
            {...other}
            ref={ref}
            dir={direction}
            className={`${className || ''} ${styles.comboPopper}`}
            placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
            modifiers={[
                ...(modifiers || []),
                { name: 'offset', options: { offset: [0, 8] } },
                { name: 'preventOverflow', options: { padding: 0 } },
                sameWidthModifier,
            ]}
            style={{
                ...style,
                width,
                minWidth: width,
            }}
        />
    );
});
ComboPopper.displayName = 'ComboPopper';

const FIELD_SIZE_CLASS = {
    wide: styles.fieldWide,
    grow: styles.fieldGrow,
    narrow: styles.fieldNarrow,
    date: styles.fieldDate,
};

const Field = ({
    fieldName,
    title,
    size,
    columnNumber,
    dragHint,
    dropSide,
    isDragging,
    onTitleDragStart,
    onTitleDragEnd,
    onDragOver,
    onDrop,
    children,
}) => (
    <div
        className={[
            styles.field,
            FIELD_SIZE_CLASS[size] || '',
            isDragging ? styles.fieldDragging : '',
            dropSide === 'before' ? styles.fieldDropBefore : '',
            dropSide === 'after' ? styles.fieldDropAfter : '',
        ].filter(Boolean).join(' ')}
        data-field={fieldName}
        onDragOver={onDragOver}
        onDrop={onDrop}
    >
        <div
            className={`${styles.title} ${styles.titleDraggable}`}
            draggable
            title={dragHint}
            onDragStart={onTitleDragStart}
            onDragEnd={onTitleDragEnd}
        >
            <DragIndicatorIcon className={styles.dragHandle} aria-hidden="true" />
            {columnNumber ? <span className={styles.columnNumber}>{columnNumber}</span> : null}
            <span className={styles.titleText}>{title}</span>
        </div>
        {children}
    </div>
);

const focusElement = (el) => {
    if (!el) return;
    el.focus?.();
    if (typeof el.select === 'function' && el.tagName === 'INPUT' && el.type !== 'file') {
        try {
            el.select();
        } catch {
            /* ignore */
        }
    }
};

const AddJournalEntryModal = ({
    open,
    onClose,
    onSave,
    t,
    direction = 'rtl',
    adapterLocale,
    sortCodeIdOptions = [],
    enhancedDebitAccountOptions = [],
    enhancedCreditAccountOptions = [],
    handleNoOptionCreditDebitClick,
    searchTermDebit,
    setSearchTermDebit,
    searchTermCredit,
    setSearchTermCredit,
    sortCodeInputRef,
    applyAccountCardToDraftRef,
}) => {
    const [draftRows, setDraftRows] = useState(() => [createEmptyDraftRow()]);
    const [addAsProcessed, setAddAsProcessed] = useState(true);
    /** Only one selector open: { rowId, fieldName } | null */
    const [activeAutocomplete, setActiveAutocomplete] = useState(null);
    const [columnOrder, setColumnOrder] = useState(loadColumnOrder);
    const [draggedColumn, setDraggedColumn] = useState(null);
    const [dropTargetColumn, setDropTargetColumn] = useState(null);
    const [activeRowId, setActiveRowId] = useState(null);

    const fieldRefs = useRef({});
    const draftRowsRef = useRef(draftRows);
    const columnOrderRef = useRef(columnOrder);
    const pendingAdvanceRef = useRef(null); // { rowId, fieldName }
    const pendingFocusRef = useRef(null); // { rowId, fieldName, open }
    const accountCardTargetRef = useRef(null); // { rowId, fieldName }

    useEffect(() => {
        draftRowsRef.current = draftRows;
    }, [draftRows]);

    useEffect(() => {
        columnOrderRef.current = columnOrder;
        try {
            window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
        } catch {
            /* ignore */
        }
    }, [columnOrder]);

    const getNavOrder = useCallback(() => [...columnOrderRef.current, ROW_END_FIELD], []);

    const setFieldRef = useCallback((rowId, fieldName, el) => {
        const key = `${rowId}:${fieldName}`;
        if (el) fieldRefs.current[key] = el;
        else delete fieldRefs.current[key];

        if (fieldName === 'sortCodeId' && sortCodeInputRef && draftRowsRef.current[0]?.id === rowId) {
            sortCodeInputRef.current = el;
        }
    }, [sortCodeInputRef]);

    const isComboOpen = useCallback((rowId, fieldName) => (
        activeAutocomplete?.rowId === rowId && activeAutocomplete?.fieldName === fieldName
    ), [activeAutocomplete]);

    const openCombo = useCallback((rowId, fieldName) => {
        setActiveAutocomplete({ rowId, fieldName });
    }, []);

    const closeCombo = useCallback((rowId, fieldName) => {
        setActiveAutocomplete((prev) => (
            prev?.rowId === rowId && prev?.fieldName === fieldName ? null : prev
        ));
    }, []);

    const focusField = useCallback((rowId, fieldName) => {
        const run = () => focusElement(fieldRefs.current[`${rowId}:${fieldName}`]);
        requestAnimationFrame(() => setTimeout(run, 0));
    }, []);

    const focusNextField = useCallback((rowId, currentField) => {
        const order = getNavOrder();
        const index = order.indexOf(currentField);
        if (index < 0 || index >= order.length - 1) return;

        const nextField = order[index + 1];
        focusField(rowId, nextField);
        if (AUTO_OPEN_FIELDS.has(nextField)) {
            openCombo(rowId, nextField);
        } else {
            setActiveAutocomplete(null);
        }
    }, [focusField, getNavOrder, openCombo]);

    /** Field `step` positions away, wrapping into the previous / next row. */
    const focusAdjacentField = useCallback((rowId, currentField, step) => {
        const order = getNavOrder();
        const index = order.indexOf(currentField);
        if (index < 0) return;
        setActiveAutocomplete(null);
        if (order[index + step]) {
            focusField(rowId, order[index + step]);
            return;
        }
        const rows = draftRowsRef.current;
        const adjacentRow = rows[rows.findIndex((row) => row.id === rowId) + step];
        if (adjacentRow) focusField(adjacentRow.id, step > 0 ? order[0] : order[order.length - 1]);
    }, [focusField, getNavOrder]);

    /** Same field in the row `step` rows away. Returns false when there is no such row. */
    const focusFieldInSiblingRow = useCallback((rowId, fieldName, step) => {
        const rows = draftRowsRef.current;
        const index = rows.findIndex((row) => row.id === rowId);
        const target = index < 0 ? null : rows[index + step];
        if (!target) return false;
        setActiveAutocomplete(null);
        focusField(target.id, fieldName);
        return true;
    }, [focusField]);

    const clearPendingAdvance = useCallback((rowId, fieldName) => {
        const pending = pendingAdvanceRef.current;
        if (pending?.rowId === rowId && pending?.fieldName === fieldName) {
            pendingAdvanceRef.current = null;
        }
    }, []);

    const markPendingAdvance = useCallback((rowId, fieldName) => {
        pendingAdvanceRef.current = { rowId, fieldName };
    }, []);

    const consumePendingAdvance = useCallback((rowId, fieldName) => {
        const pending = pendingAdvanceRef.current;
        if (pending?.rowId !== rowId || pending?.fieldName !== fieldName) return false;
        pendingAdvanceRef.current = null;
        return true;
    }, []);

    const advanceAfterCommit = useCallback((rowId, fieldName) => {
        closeCombo(rowId, fieldName);
        focusNextField(rowId, fieldName);
    }, [closeCombo, focusNextField]);

    const updateRow = useCallback((rowId, patchOrUpdater) => {
        setDraftRows((prev) => prev.map((row) => {
            if (row.id !== rowId) return row;
            const patch = typeof patchOrUpdater === 'function' ? patchOrUpdater(row) : patchOrUpdater;
            const updated = { ...row, ...patch };

            if (Object.prototype.hasOwnProperty.call(patch, 'amount')
                || Object.prototype.hasOwnProperty.call(patch, 'vat')
                || Object.prototype.hasOwnProperty.call(patch, 'documentDate')) {
                const amount = parseFloat(updated.amount) || 0;
                const vat = updated.vat;
                const documentDate = updated.documentDate || new Date();
                updated.vatAmount = calculateVatAmount(amount, vat, documentDate);
                updated.netAmount = amount - updated.vatAmount;
            }

            if (Object.prototype.hasOwnProperty.call(patch, 'sortCodeId')) {
                const sortCode = sortCodeIdOptions.find((sc) => sc.sortCodeId === updated.sortCodeId);
                if (sortCode) {
                    if (sortCode.vat != null) updated.vat = sortCode.vat;
                    if (sortCode.creditAccount) updated.creditAccount = sortCode.creditAccount;
                    if (sortCode.debitAccount) updated.debitAccount = sortCode.debitAccount;
                }
            }

            return updated;
        }));
    }, [sortCodeIdOptions]);

    // Bridge for AddAccountCardModal → apply to the correct draft row
    useEffect(() => {
        if (!applyAccountCardToDraftRef) return undefined;
        applyAccountCardToDraftRef.current = (newlyAddedCardId, type, addedAccountCaseId) => {
            const target = accountCardTargetRef.current;
            if (!target?.rowId) return;
            updateRow(target.rowId, {
                [type]: newlyAddedCardId,
                ...(addedAccountCaseId != null && addedAccountCaseId !== ''
                    ? { accountCaseId: addedAccountCaseId }
                    : {}),
            });
            accountCardTargetRef.current = null;
        };
        return () => {
            applyAccountCardToDraftRef.current = null;
        };
    }, [applyAccountCardToDraftRef, updateRow]);

    const requestAddAccountCard = useCallback((searchTerm, fieldName, rowId) => {
        accountCardTargetRef.current = { rowId, fieldName };
        pendingAdvanceRef.current = null;
        setActiveAutocomplete(null);
        handleNoOptionCreditDebitClick?.(searchTerm, fieldName);
    }, [handleNoOptionCreditDebitClick]);

    /**
     * MUI Autocomplete Enter:
     * - closed → open only (keep value)
     * - open → let MUI commit first, then advance via onChange / safe same-value fallback
     */
    const handleComboKeyDown = useCallback((event, {
        rowId,
        fieldName,
        inputPropsOnKeyDown,
    }) => {
        if (event.key !== 'Enter' || event.shiftKey) {
            inputPropsOnKeyDown?.(event);
            return;
        }

        const isOpen = activeAutocomplete?.rowId === rowId && activeAutocomplete?.fieldName === fieldName;

        if (!isOpen) {
            // Open only — do not advance, do not clear value.
            event.preventDefault();
            openCombo(rowId, fieldName);
            return;
        }

        // Let MUI process Enter and commit the highlighted option FIRST.
        markPendingAdvance(rowId, fieldName);
        inputPropsOnKeyDown?.(event);
        // Prevent form submit after MUI has handled selection.
        event.preventDefault();
    }, [activeAutocomplete, markPendingAdvance, openCombo]);

    const handleComboClose = useCallback((rowId, fieldName, reason) => {
        closeCombo(rowId, fieldName);

        if (reason === 'selectOption') {
            // Same-value confirm: MUI may skip onChange. Only advance if value is already committed.
            queueMicrotask(() => {
                if (!consumePendingAdvance(rowId, fieldName)) return;
                const row = draftRowsRef.current.find((item) => item.id === rowId);
                if (fieldHasCommittedValue(row, fieldName)) {
                    advanceAfterCommit(rowId, fieldName);
                }
            });
            return;
        }

        clearPendingAdvance(rowId, fieldName);
    }, [advanceAfterCommit, clearPendingAdvance, closeCombo, consumePendingAdvance]);

    const handlePlainEnter = useCallback((event, rowId, fieldName) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        focusNextField(rowId, fieldName);
    }, [focusNextField]);

    const handleAmountEnter = useCallback((event, rowId) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();

        const row = draftRowsRef.current.find((item) => item.id === rowId);
        const evaluated = evaluateAmountExpression(row?.amount ?? event.target?.value);
        if (evaluated != null) {
            flushSync(() => {
                updateRow(rowId, { amount: evaluated });
            });
        }
        focusNextField(rowId, 'amount');
    }, [focusNextField, updateRow]);

    const handleDateEnter = useCallback((event, rowId, fieldName) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();

        const parsed = parseSmartDateInput(event.target?.value);
        if (parsed) {
            flushSync(() => {
                updateRow(rowId, { [fieldName]: parsed });
            });
        }
        focusNextField(rowId, fieldName);
    }, [focusNextField, updateRow]);

    /** Inserts an empty row after `afterRowId` (or at the end) and focuses its first column. */
    const addRow = useCallback((afterRowId = null) => {
        const newRow = createEmptyDraftRow();
        newRow.addAsProcessed = addAsProcessed;
        const firstField = columnOrderRef.current[0];
        pendingFocusRef.current = {
            rowId: newRow.id,
            fieldName: firstField,
            open: AUTO_OPEN_FIELDS.has(firstField),
        };
        setActiveAutocomplete(null);
        setDraftRows((prev) => {
            const index = afterRowId ? prev.findIndex((row) => row.id === afterRowId) : -1;
            if (index < 0) return [...prev, newRow];
            return [...prev.slice(0, index + 1), newRow, ...prev.slice(index + 1)];
        });
    }, [addAsProcessed]);

    const handleDocumentEnter = useCallback((event, rowId) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        event.stopPropagation();

        const rows = draftRowsRef.current;
        const index = rows.findIndex((row) => row.id === rowId);
        if (index < 0) return;

        const nextExisting = rows[index + 1];
        if (nextExisting) {
            const firstField = columnOrderRef.current[0];
            focusField(nextExisting.id, firstField);
            if (AUTO_OPEN_FIELDS.has(firstField)) openCombo(nextExisting.id, firstField);
            else setActiveAutocomplete(null);
            return;
        }

        addRow(rowId);
    }, [addRow, focusField, openCombo]);

    const handleDeleteRow = useCallback((rowId) => {
        if (pendingAdvanceRef.current?.rowId === rowId) {
            pendingAdvanceRef.current = null;
        }
        if (accountCardTargetRef.current?.rowId === rowId) {
            accountCardTargetRef.current = null;
        }
        setActiveAutocomplete((prev) => (prev?.rowId === rowId ? null : prev));
        Object.keys(fieldRefs.current).forEach((key) => {
            if (key.startsWith(`${rowId}:`)) delete fieldRefs.current[key];
        });
        setDraftRows((prev) => {
            if (prev.length <= 1) return prev;
            const next = prev.filter((row) => row.id !== rowId);
            return next.length ? next : prev;
        });
    }, []);

    /** Keyboard delete: keeps focus on the same column in the neighbouring row. */
    const deleteRowAndRefocus = useCallback((rowId, fieldName) => {
        const rows = draftRowsRef.current;
        if (rows.length <= 1) return;
        const index = rows.findIndex((row) => row.id === rowId);
        if (index < 0) return;
        const neighbour = rows[index + 1] || rows[index - 1];
        handleDeleteRow(rowId);
        if (neighbour) focusField(neighbour.id, fieldName);
    }, [focusField, handleDeleteRow]);

    const moveColumn = useCallback((fieldName, step) => {
        setColumnOrder((prev) => {
            const from = prev.indexOf(fieldName);
            return moveItem(prev, from, from + step);
        });
    }, []);

    // After a new draft row mounts, focus + open its Code ID.
    useEffect(() => {
        const pending = pendingFocusRef.current;
        if (!pending) return;
        if (!draftRows.some((row) => row.id === pending.rowId)) return;
        pendingFocusRef.current = null;
        focusField(pending.rowId, pending.fieldName);
        if (pending.open) openCombo(pending.rowId, pending.fieldName);
    }, [draftRows, focusField, openCombo]);

    // Reset when modal opens.
    useEffect(() => {
        if (!open) {
            setActiveAutocomplete(null);
            pendingAdvanceRef.current = null;
            return undefined;
        }
        const first = createEmptyDraftRow();
        setDraftRows([first]);
        setAddAsProcessed(true);
        setActiveAutocomplete(null);
        const timer = setTimeout(() => {
            focusField(first.id, columnOrderRef.current[0]);
        }, 50);
        return () => clearTimeout(timer);
    }, [open, focusField]);

    const handleSaveAll = useCallback(async () => {
        const rows = draftRowsRef.current;
        const candidates = rows.filter((row) => !isBlankDraft(row));
        if (!candidates.length) {
            const first = rows[0];
            if (first) focusField(first.id, 'amount');
            return;
        }

        const invalid = candidates.find((row) => evaluateAmountExpression(row.amount) == null);
        if (invalid) {
            focusField(invalid.id, 'amount');
            setActiveAutocomplete(null);
            return;
        }

        const cleaned = candidates.map((row) => {
            const { id, ...rest } = row;
            return {
                ...rest,
                amount: evaluateAmountExpression(row.amount),
                addAsProcessed,
            };
        });

        await onSave?.(cleaned);

        const fresh = createEmptyDraftRow();
        fresh.addAsProcessed = addAsProcessed;
        setDraftRows([fresh]);
        setActiveAutocomplete(null);
        setTimeout(() => focusField(fresh.id, columnOrderRef.current[0]), 0);
    }, [addAsProcessed, focusField, onSave]);

    /**
     * Modal-wide shortcuts. Runs in the capture phase so MUI Autocomplete / DatePicker
     * never see the key combos we own (they would otherwise open popups or change dates).
     */
    const handleShortcutKeyDown = useCallback((event) => {
        const mod = event.ctrlKey || event.metaKey;
        const { key, code, altKey, shiftKey } = event;
        const consume = () => {
            event.preventDefault();
            event.stopPropagation();
        };
        const cell = getCellFromTarget(event.target);

        if (mod && !altKey && !shiftKey && key === 'Enter') {
            consume();
            if (!event.repeat) addRow(cell?.rowId);
            return;
        }

        if (mod && !altKey && !shiftKey && code === 'KeyS') {
            consume();
            if (!event.repeat) handleSaveAll();
            return;
        }

        if (!cell) return;
        const { rowId, fieldName } = cell;

        if (mod && shiftKey && !altKey && (key === 'Backspace' || key === 'Delete')) {
            consume();
            deleteRowAndRefocus(rowId, fieldName);
            return;
        }

        if (altKey && shiftKey && !mod && (key === 'ArrowLeft' || key === 'ArrowRight')) {
            if (fieldName === ROW_END_FIELD) return;
            consume();
            const towardsStart = (key === 'ArrowLeft') === (direction !== 'rtl');
            moveColumn(fieldName, towardsStart ? -1 : 1);
            focusField(rowId, fieldName);
            return;
        }

        if ((key === 'ArrowUp' || key === 'ArrowDown') && !mod && !shiftKey
            && (altKey || ARROW_ROW_NAV_FIELDS.has(fieldName))) {
            const moved = focusFieldInSiblingRow(rowId, fieldName, key === 'ArrowUp' ? -1 : 1);
            if (moved || altKey) consume();
            return;
        }

        if (altKey && !mod && !shiftKey && /^(Digit|Numpad)[1-9]$/.test(code)) {
            const target = getNavOrder()[Number(code.slice(-1)) - 1];
            if (!target) return;
            consume();
            setActiveAutocomplete(null);
            focusField(rowId, target);
            return;
        }

        if ((key === 'ArrowLeft' || key === 'ArrowRight') && !mod && !altKey && !shiftKey) {
            const step = (key === 'ArrowRight') === (direction !== 'rtl') ? 1 : -1;
            const input = event.target;
            const hasCaret = input?.tagName === 'INPUT' && typeof input.selectionStart === 'number';
            const { selectionStart, selectionEnd, value = '' } = hasCaret ? input : {};
            const wholeTextSelected = !value.length || (selectionStart === 0 && selectionEnd === value.length);

            if (hasCaret && !wholeTextSelected) {
                // Let the input / date field move its caret or section first; leave the field
                // only when the key had nothing left to move.
                setTimeout(() => {
                    if (document.activeElement !== input) return;
                    if (input.selectionStart !== selectionStart || input.selectionEnd !== selectionEnd) return;
                    focusAdjacentField(rowId, fieldName, step);
                }, 0);
                return;
            }

            consume();
            focusAdjacentField(rowId, fieldName, step);
            return;
        }

        if (key === 'Enter' && shiftKey && !mod && !altKey) {
            consume();
            focusAdjacentField(rowId, fieldName, -1);
        }
    }, [
        addRow,
        deleteRowAndRefocus,
        direction,
        focusAdjacentField,
        focusField,
        focusFieldInSiblingRow,
        getNavOrder,
        handleSaveAll,
        moveColumn,
    ]);

    const handleRowsFocusCapture = useCallback((event) => {
        const cell = getCellFromTarget(event.target);
        if (cell) setActiveRowId(cell.rowId);
    }, []);

    const renderComboPopper = useCallback((props) => (
        <ComboPopper {...props} direction={direction} />
    ), [direction]);

    if (!open) return null;

    const inputProps = { disableUnderline: true };
    const vatOptions = [
        { value: 0, label: '0%' },
        { value: 16, label: `${t('AccountingTax')} (16%)` },
        { value: 25, label: '25%' },
        { value: 66, label: '66%' },
        { value: 100, label: '100%' },
    ];

    const hasAnyAmount = draftRows.some((row) => row.amount != null && row.amount !== '');

    const assignComboInputRef = (rowId, fieldName, params, el) => {
        setFieldRef(rowId, fieldName, el);
        if (typeof params.inputRef === 'function') params.inputRef(el);
        else if (params.inputRef) params.inputRef.current = el;
    };

    const endColumnDrag = () => {
        setDraggedColumn(null);
        setDropTargetColumn(null);
    };

    const handleColumnDragStart = (event, fieldName) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(COLUMN_DRAG_MIME, fieldName);
        setActiveAutocomplete(null);
        setDraggedColumn(fieldName);
    };

    const handleColumnDragOver = (event, fieldName) => {
        if (!draggedColumn) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dropTargetColumn !== fieldName) setDropTargetColumn(fieldName);
    };

    const handleColumnDrop = (event, fieldName) => {
        if (!draggedColumn) return;
        event.preventDefault();
        setColumnOrder((prev) => moveItem(prev, prev.indexOf(draggedColumn), prev.indexOf(fieldName)));
        endColumnDrag();
    };

    const draggedIndex = columnOrder.indexOf(draggedColumn);
    const columnProps = Object.fromEntries(columnOrder.map((fieldName, index) => {
        const column = COLUMNS[fieldName];
        const isDropTarget = !!draggedColumn && draggedColumn !== fieldName && dropTargetColumn === fieldName;
        return [fieldName, {
            fieldName,
            title: column.required ? `${t(column.titleKey)} *` : t(column.titleKey),
            size: column.size,
            columnNumber: index < 9 ? index + 1 : null,
            dragHint: t('DragToReorderColumn'),
            dropSide: isDropTarget ? (draggedIndex < index ? 'after' : 'before') : null,
            isDragging: draggedColumn === fieldName,
            onTitleDragStart: (event) => handleColumnDragStart(event, fieldName),
            onTitleDragEnd: endColumnDrag,
            onDragOver: (event) => handleColumnDragOver(event, fieldName),
            onDrop: (event) => handleColumnDrop(event, fieldName),
        }];
    }));

    const isCustomColumnOrder = columnOrder.some((fieldName, index) => fieldName !== DEFAULT_COLUMN_ORDER[index]);

    const shortcutHints = [
        { keys: ['Enter', 'Shift+Enter', '← / →'], label: t('JournalShortcutFields') },
        { keys: [`${ALT_KEY_LABEL}+↑ / ↓`], label: t('JournalShortcutRows') },
        { keys: [`${ALT_KEY_LABEL}+1…9`], label: t('JournalShortcutJumpColumn') },
        { keys: [`${MOD_KEY_LABEL}+Enter`], label: t('JournalShortcutAddRow') },
        { keys: [`${MOD_KEY_LABEL}+Shift+Backspace`], label: t('JournalShortcutDeleteRow') },
        { keys: [`${ALT_KEY_LABEL}+Shift+← / →`], label: t('JournalShortcutMoveColumn') },
        { keys: [`${MOD_KEY_LABEL}+S`], label: t('JournalShortcutSave') },
    ];

    const renderRow = (row) => {
        const sortCode = sortCodeIdOptions.find((o) => o.sortCodeId === row.sortCodeId);
        const selectedVat = vatOptions.find((o) => o.value === row.vat) || vatOptions[0];
        const hasAmount = row.amount != null && row.amount !== '';

        const cells = {
            sortCodeId: (
                <Field {...columnProps.sortCodeId}>
                    <Autocomplete
                        className={styles.combo}
                        size="small"
                        options={sortCodeIdOptions}
                        getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName}`}
                        value={sortCode || null}
                        onChange={(e, v) => {
                            const shouldAdvance = !!pendingAdvanceRef.current
                                && pendingAdvanceRef.current.rowId === row.id
                                && pendingAdvanceRef.current.fieldName === 'sortCodeId';
                            flushSync(() => {
                                updateRow(row.id, { sortCodeId: v?.sortCodeId || '' });
                            });
                            if (shouldAdvance && consumePendingAdvance(row.id, 'sortCodeId')) {
                                advanceAfterCommit(row.id, 'sortCodeId');
                            }
                        }}
                        open={isComboOpen(row.id, 'sortCodeId')}
                        onOpen={() => openCombo(row.id, 'sortCodeId')}
                        onClose={(e, reason) => handleComboClose(row.id, 'sortCodeId', reason)}
                        PopperComponent={renderComboPopper}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                className={styles.styleInput}
                                placeholder={t('Code ID')}
                                InputProps={{ ...params.InputProps, ...inputProps }}
                                inputRef={(el) => assignComboInputRef(row.id, 'sortCodeId', params, el)}
                                onKeyDown={(e) => handleComboKeyDown(e, {
                                    rowId: row.id,
                                    fieldName: 'sortCodeId',
                                    inputPropsOnKeyDown: params.inputProps?.onKeyDown,
                                })}
                            />
                        )}
                        fullWidth
                    />
                </Field>
            ),
            debitAccount: (
                <Field {...columnProps.debitAccount}>
                    <Autocomplete
                        className={styles.combo}
                        size="small"
                        options={enhancedDebitAccountOptions}
                        getOptionLabel={(o) => (o.isAddNew ? o.name : `${o.cardId} - ${o.name}`)}
                        value={enhancedDebitAccountOptions.find((o) => o.cardId === row.debitAccount) || null}
                        onChange={(e, v) => {
                            if (v?.isAddNew) {
                                clearPendingAdvance(row.id, 'debitAccount');
                                requestAddAccountCard('', 'debitAccount', row.id);
                                return;
                            }
                            const shouldAdvance = !!pendingAdvanceRef.current
                                && pendingAdvanceRef.current.rowId === row.id
                                && pendingAdvanceRef.current.fieldName === 'debitAccount';
                            flushSync(() => {
                                if (!v) {
                                    const credit = enhancedCreditAccountOptions.find(
                                        (o) => o.cardId === row.creditAccount && !o.isAddNew
                                    );
                                    updateRow(row.id, {
                                        debitAccount: '',
                                        accountCaseId: credit?.accountCaseId || '',
                                    });
                                } else {
                                    updateRow(row.id, {
                                        debitAccount: v.cardId || '',
                                        ...(v.accountCaseId != null && v.accountCaseId !== ''
                                            ? { accountCaseId: v.accountCaseId }
                                            : {}),
                                    });
                                }
                            });
                            if (shouldAdvance && consumePendingAdvance(row.id, 'debitAccount')) {
                                advanceAfterCommit(row.id, 'debitAccount');
                            }
                        }}
                        onInputChange={(e, v) => setSearchTermDebit?.(v)}
                        open={isComboOpen(row.id, 'debitAccount')}
                        onOpen={() => openCombo(row.id, 'debitAccount')}
                        onClose={(e, reason) => handleComboClose(row.id, 'debitAccount', reason)}
                        PopperComponent={renderComboPopper}
                        noOptionsText={
                            <button
                                type="button"
                                className={styles.addOption}
                                onClick={() => requestAddAccountCard(searchTermDebit, 'debitAccount', row.id)}
                            >
                                <span className={styles.addOptionPlus} aria-hidden="true">+</span>
                                <span className={styles.addOptionLabel}>
                                    {t('AddAccountCard')}{searchTermDebit ? ` "${searchTermDebit}"` : ''}
                                </span>
                            </button>
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                className={styles.styleInput}
                                placeholder={t('Debit Account')}
                                InputProps={{ ...params.InputProps, ...inputProps }}
                                inputRef={(el) => assignComboInputRef(row.id, 'debitAccount', params, el)}
                                onKeyDown={(e) => handleComboKeyDown(e, {
                                    rowId: row.id,
                                    fieldName: 'debitAccount',
                                    inputPropsOnKeyDown: params.inputProps?.onKeyDown,
                                })}
                            />
                        )}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            if (option.isAddNew) {
                                return (
                                    <li key={key} {...optionProps} className={`${optionProps.className || ''} ${styles.addOption}`}>
                                        <span className={styles.addOptionPlus} aria-hidden="true">+</span>
                                        <span className={styles.addOptionLabel}>{option.name}</span>
                                    </li>
                                );
                            }
                            return <li key={key} {...optionProps}>{`${option.cardId} - ${option.name}`}</li>;
                        }}
                        fullWidth
                    />
                </Field>
            ),
            creditAccount: (
                <Field {...columnProps.creditAccount}>
                    <Autocomplete
                        className={styles.combo}
                        size="small"
                        options={enhancedCreditAccountOptions}
                        getOptionLabel={(o) => (o.isAddNew ? o.name : `${o.cardId} - ${o.name}`)}
                        value={enhancedCreditAccountOptions.find((o) => o.cardId === row.creditAccount) || null}
                        onChange={(e, v) => {
                            if (v?.isAddNew) {
                                clearPendingAdvance(row.id, 'creditAccount');
                                requestAddAccountCard('', 'creditAccount', row.id);
                                return;
                            }
                            const shouldAdvance = !!pendingAdvanceRef.current
                                && pendingAdvanceRef.current.rowId === row.id
                                && pendingAdvanceRef.current.fieldName === 'creditAccount';
                            flushSync(() => {
                                if (!v) {
                                    const debit = enhancedDebitAccountOptions.find(
                                        (o) => o.cardId === row.debitAccount && !o.isAddNew
                                    );
                                    updateRow(row.id, {
                                        creditAccount: '',
                                        accountCaseId: debit?.accountCaseId || '',
                                    });
                                } else {
                                    updateRow(row.id, {
                                        creditAccount: v.cardId || '',
                                        ...(v.accountCaseId != null && v.accountCaseId !== ''
                                            ? { accountCaseId: v.accountCaseId }
                                            : {}),
                                    });
                                }
                            });
                            if (shouldAdvance && consumePendingAdvance(row.id, 'creditAccount')) {
                                advanceAfterCommit(row.id, 'creditAccount');
                            }
                        }}
                        onInputChange={(e, v) => setSearchTermCredit?.(v)}
                        open={isComboOpen(row.id, 'creditAccount')}
                        onOpen={() => openCombo(row.id, 'creditAccount')}
                        onClose={(e, reason) => handleComboClose(row.id, 'creditAccount', reason)}
                        PopperComponent={renderComboPopper}
                        noOptionsText={
                            <button
                                type="button"
                                className={styles.addOption}
                                onClick={() => requestAddAccountCard(searchTermCredit, 'creditAccount', row.id)}
                            >
                                <span className={styles.addOptionPlus} aria-hidden="true">+</span>
                                <span className={styles.addOptionLabel}>
                                    {t('AddAccountCard')}{searchTermCredit ? ` "${searchTermCredit}"` : ''}
                                </span>
                            </button>
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                className={styles.styleInput}
                                placeholder={t('Credit Account')}
                                InputProps={{ ...params.InputProps, ...inputProps }}
                                inputRef={(el) => assignComboInputRef(row.id, 'creditAccount', params, el)}
                                onKeyDown={(e) => handleComboKeyDown(e, {
                                    rowId: row.id,
                                    fieldName: 'creditAccount',
                                    inputPropsOnKeyDown: params.inputProps?.onKeyDown,
                                })}
                            />
                        )}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;
                            if (option.isAddNew) {
                                return (
                                    <li key={key} {...optionProps} className={`${optionProps.className || ''} ${styles.addOption}`}>
                                        <span className={styles.addOptionPlus} aria-hidden="true">+</span>
                                        <span className={styles.addOptionLabel}>{option.name}</span>
                                    </li>
                                );
                            }
                            return <li key={key} {...optionProps}>{`${option.cardId} - ${option.name}`}</li>;
                        }}
                        fullWidth
                    />
                </Field>
            ),
            vat: (
                <Field {...columnProps.vat}>
                    <div
                        className={`${styles.vatField}${isComboOpen(row.id, 'vat') ? ` ${styles.vatFieldOpen}` : ''}`}
                    >
                        <AutocompleteSelectFilter
                            options={vatOptions}
                            selectedValues={selectedVat}
                            onChange={(_, option) => {
                                flushSync(() => {
                                    updateRow(row.id, { vat: parseInt(option?.value ?? 0, 10) });
                                });
                            }}
                            placeholder={t('VAT')}
                            isMultiple={false}
                            direction={direction}
                            open={isComboOpen(row.id, 'vat')}
                            onOpenChange={(nextOpen) => {
                                if (nextOpen) openCombo(row.id, 'vat');
                                else closeCombo(row.id, 'vat');
                            }}
                            triggerRef={(el) => setFieldRef(row.id, 'vat', el)}
                            onSelectConfirm={() => {
                                advanceAfterCommit(row.id, 'vat');
                            }}
                            portalMenu
                        />
                    </div>
                </Field>
            ),
            amount: (
                <Field {...columnProps.amount}>
                    <TextField
                        variant="standard"
                        className={`${styles.styleInput}${hasAmount ? '' : ` ${styles.styleInputRequired}`}`}
                        fullWidth
                        type="text"
                        inputMode="decimal"
                        value={row.amount ?? ''}
                        onChange={(e) => updateRow(row.id, { amount: e.target.value })}
                        onBlur={(e) => {
                            const evaluated = evaluateAmountExpression(e.target.value);
                            if (evaluated != null && String(e.target.value).trim() !== String(evaluated)) {
                                updateRow(row.id, { amount: evaluated });
                            }
                        }}
                        required
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'amount', el)}
                        onKeyDown={(e) => handleAmountEnter(e, row.id)}
                        placeholder={t('Amount')}
                    />
                </Field>
            ),
            documentDate: (
                <Field {...columnProps.documentDate}>
                    <DatePicker
                        value={row.documentDate}
                        onChange={(d) => updateRow(row.id, { documentDate: d })}
                        format="dd/MM/yyyy"
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                fullWidth: true,
                                className: styles.styleInput,
                                InputProps: inputProps,
                                inputRef: (el) => setFieldRef(row.id, 'documentDate', el),
                                onKeyDown: (e) => handleDateEnter(e, row.id, 'documentDate'),
                            },
                        }}
                    />
                </Field>
            ),
            uploadDate: (
                <Field {...columnProps.uploadDate}>
                    <DatePicker
                        value={row.uploadDate}
                        onChange={(d) => updateRow(row.id, { uploadDate: d })}
                        format="dd/MM/yyyy"
                        slotProps={{
                            textField: {
                                variant: 'standard',
                                fullWidth: true,
                                className: styles.styleInput,
                                InputProps: inputProps,
                                inputRef: (el) => setFieldRef(row.id, 'uploadDate', el),
                                onKeyDown: (e) => handleDateEnter(e, row.id, 'uploadDate'),
                            },
                        }}
                    />
                </Field>
            ),
            accountCaseId: (
                <Field {...columnProps.accountCaseId}>
                    <TextField
                        variant="standard"
                        className={styles.styleInput}
                        fullWidth
                        value={row.accountCaseId || ''}
                        onChange={(e) => updateRow(row.id, { accountCaseId: e.target.value })}
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'accountCaseId', el)}
                        onKeyDown={(e) => handlePlainEnter(e, row.id, 'accountCaseId')}
                        placeholder={t('CompanyId')}
                    />
                </Field>
            ),
            confirmationNumber: (
                <Field {...columnProps.confirmationNumber}>
                    <TextField
                        variant="standard"
                        className={styles.styleInput}
                        fullWidth
                        value={row.confirmationNumber || ''}
                        onChange={(e) => updateRow(row.id, { confirmationNumber: e.target.value })}
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'confirmationNumber', el)}
                        onKeyDown={(e) => handlePlainEnter(e, row.id, 'confirmationNumber')}
                        placeholder={t('ConfirmationNumber')}
                    />
                </Field>
            ),
            reference: (
                <Field {...columnProps.reference}>
                    <TextField
                        variant="standard"
                        className={styles.styleInput}
                        fullWidth
                        value={row.reference || ''}
                        onChange={(e) => updateRow(row.id, { reference: e.target.value })}
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'reference', el)}
                        onKeyDown={(e) => handlePlainEnter(e, row.id, 'reference')}
                        placeholder={t('Reference')}
                    />
                </Field>
            ),
            reference2: (
                <Field {...columnProps.reference2}>
                    <TextField
                        variant="standard"
                        className={styles.styleInput}
                        fullWidth
                        value={row.reference2 || ''}
                        onChange={(e) => updateRow(row.id, { reference2: e.target.value })}
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'reference2', el)}
                        onKeyDown={(e) => handlePlainEnter(e, row.id, 'reference2')}
                        placeholder={t('Reference2')}
                    />
                </Field>
            ),
            description: (
                <Field {...columnProps.description}>
                    <TextField
                        variant="standard"
                        className={styles.styleInput}
                        fullWidth
                        value={row.description || ''}
                        onChange={(e) => updateRow(row.id, { description: e.target.value })}
                        InputProps={inputProps}
                        inputRef={(el) => setFieldRef(row.id, 'description', el)}
                        onKeyDown={(e) => handlePlainEnter(e, row.id, 'description')}
                        placeholder={t('Description')}
                    />
                </Field>
            ),
        };

        return (
            <div
                key={row.id}
                className={[
                    styles.fieldsGrid,
                    isComboOpen(row.id, 'vat') ? styles.fieldsGridElevated : '',
                    activeRowId === row.id && draftRows.length > 1 ? styles.fieldsGridActive : '',
                ].filter(Boolean).join(' ')}
                data-row-id={row.id}
            >
                {columnOrder.map((fieldName) => (
                    <React.Fragment key={fieldName}>{cells[fieldName]}</React.Fragment>
                ))}

                <div className={styles.rowEndActions} data-field={ROW_END_FIELD}>
                    <IconUpload
                        title={t('Document')}
                        hasFiles={row.documents?.length > 0}
                        fileCount={row.documents?.length || 0}
                        filesLabel={t('Files')}
                        onChange={(e) => updateRow(row.id, { documents: Array.from(e.target.files) })}
                        rootRef={(el) => setFieldRef(row.id, 'documents', el)}
                        onEnter={(e) => handleDocumentEnter(e, row.id)}
                    />
                    {draftRows.length > 1 ? (
                        <Tooltip title={`${t('Delete') || 'Delete'} (${MOD_KEY_LABEL}+Shift+Backspace)`}>
                            <button
                                type="button"
                                className={styles.deleteRowBtn}
                                onClick={() => handleDeleteRow(row.id)}
                                aria-label={t('Delete') || 'Delete'}
                            >
                                <DeleteOutlineIcon className={styles.deleteRowIcon} />
                            </button>
                        </Tooltip>
                    ) : null}
                </div>
            </div>
        );
    };

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div
                    dir={direction}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDownCapture={handleShortcutKeyDown}
                >
                    <div className={styles.modal}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                            <form
                                className={styles.scroll}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                <div className={styles.header}>
                                    <Tooltip title={t('Close') || 'Close'}>
                                        <IconButton onClick={onClose} className={styles.closeIcon}>
                                            <CancelIcon className={styles.closeIcon} />
                                        </IconButton>
                                    </Tooltip>
                                    <div>{t('AddingNewJournalEntry') || 'Adding New Journal Entry'}</div>
                                    {isCustomColumnOrder ? (
                                        <button
                                            type="button"
                                            className={styles.headerAction}
                                            onClick={() => setColumnOrder(DEFAULT_COLUMN_ORDER)}
                                        >
                                            {t('ResetColumnOrder')}
                                        </button>
                                    ) : <div />}
                                </div>

                                <div className={styles.help}>
                                    <div>{t('PressEnterToSave')}</div>
                                    <div className={styles.shortcuts}>
                                        {shortcutHints.map(({ keys, label }) => (
                                            <span key={label} className={styles.shortcut}>
                                                {keys.map((combo) => (
                                                    <kbd key={combo} className={styles.kbd}>{combo}</kbd>
                                                ))}
                                                <span>{label}</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className={styles.rows} onFocusCapture={handleRowsFocusCapture}>
                                    {draftRows.map((row) => renderRow(row))}
                                </div>

                                <button
                                    type="button"
                                    className={styles.addRowBtn}
                                    onClick={() => addRow()}
                                >
                                    <AddIcon className={styles.addRowIcon} aria-hidden="true" />
                                    <span>{t('AddRow')}</span>
                                    <kbd className={styles.kbd}>{`${MOD_KEY_LABEL}+Enter`}</kbd>
                                </button>

                                <div className={styles.footer}>
                                    <div className={styles.processed}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!addAsProcessed}
                                                    onChange={(e) => setAddAsProcessed(e.target.checked)}
                                                    size="small"
                                                    sx={{ color: '#304fff', '&.Mui-checked': { color: '#304fff' } }}
                                                />
                                            }
                                            label={t('MarkAsProcessed') || 'Mark as processed'}
                                        />
                                    </div>
                                    <button type="button" className={styles.cancel} onClick={onClose}>
                                        {t('Cancel') || 'Cancel'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.save}
                                        disabled={!hasAnyAmount}
                                        onClick={handleSaveAll}
                                    >
                                        {t('Save') || 'Save'}
                                    </button>
                                </div>
                            </form>
                        </LocalizationProvider>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

const IconUpload = ({ title, hasFiles, fileCount, filesLabel, onChange, rootRef, onEnter }) => (
    <label
        ref={rootRef}
        className={`${styles.uploadField}${hasFiles ? ` ${styles.uploadFieldHasFiles}` : ''}`}
        title={hasFiles ? `${fileCount} ${filesLabel}` : title}
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey) return;
            onEnter?.(e);
        }}
    >
        <span className={styles.uploadTitle}>{title}</span>
        <span className={styles.uploadIconWrap}>
            <UploadFileIcon className={styles.uploadIcon} />
            {hasFiles ? <span className={styles.uploadBadge}>{fileCount}</span> : null}
        </span>
        <input type="file" hidden multiple onChange={onChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
    </label>
);

export default AddJournalEntryModal;
